// store/orderStore.js
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const DEFAULT_CHAT = () => ({
  currentOrder: null,
  completedStatuses: [],
  messages: [],
  deliveryMarked: false,
  userConfirmedDelivery: false,
  specialInstructions: null,
  //  terminal flags live here so ALL consumers react ──────────────────
  taskCompleted: false,
  orderCancelled: false,
  orderMissing: false,
  cancellationReason: null,
  deliveryDisputeWindowOpen: false,
});

const useOrderStore = create(persist((set, get) => ({
  _hasHydrated: false,
  _chats: {},
  activeChatId: null,
  deliveryDenied: false,

  setHasHydrated: () => set({ _hasHydrated: true }),

  // ── chats Getter ─────────────────────────────────────────────────────────────────
  getChat: (chatId) => get()._chats[chatId] ?? DEFAULT_CHAT(),

  // ── chatId setter ───────────────────────────────────────────────────────────────── 
  setActiveChatId: (chatId) => set({ activeChatId: chatId }),

  setDeliveryDisputeWindowOpen: (chatId, val) => get()._patch(chatId, { deliveryDisputeWindowOpen: val }),

  // ── Internal helper ────────────────────────────────────────────────────────
  _patch: (chatId, partial) => set(state => ({
    _chats: {
      ...state._chats,
      [chatId]: { ...(state._chats[chatId] ?? DEFAULT_CHAT()), ...partial },
    },
  })),

  // ── Order ──────────────────────────────────────────────────────────────────
  setCurrentOrder: (chatId, orderOrUpdater) => set(state => {
    const prev = state._chats[chatId] ?? DEFAULT_CHAT();
    const next = typeof orderOrUpdater === 'function'
      ? orderOrUpdater(prev.currentOrder ?? null)
      : orderOrUpdater;
    return { _chats: { ...state._chats, [chatId]: { ...prev, currentOrder: next } } };
  }),

  deliveryDisputeWindowOpen: false,


  mergeCurrentOrder: (chatId, partial) => set(state => {
    const prev = state._chats[chatId] ?? DEFAULT_CHAT();
    return {
      _chats: {
        ...state._chats,
        [chatId]: { ...prev, currentOrder: { ...(prev.currentOrder ?? {}), ...partial } },
      },
    };
  }),

  // ── Delivery ───────────────────────────────────────────────────────────────
  setDeliveryMarked: (chatId, val) => get()._patch(chatId, { deliveryMarked: val }),
  setUserConfirmedDelivery: (chatId, val) => get()._patch(chatId, { userConfirmedDelivery: val }),

  setDeliveryDenied: (chatId, value) =>
    set(state => ({
      _chats: {
        ...state._chats,
        [chatId]: {
          ...state._chats[chatId],
          deliveryDenied: value
        }
      }
    })),

  // ── Instructions ───────────────────────────────────────────────────────────
  setSpecialInstructions: (chatId, val) => get()._patch(chatId, { specialInstructions: val }),

  // ── Status chips ───────────────────────────────────────────────────────────
  setCompletedStatuses: (chatId, valOrUpdater) => set(state => {
    const prev = state._chats[chatId] ?? DEFAULT_CHAT();
    const next = typeof valOrUpdater === 'function'
      ? valOrUpdater(prev.completedStatuses ?? [])
      : valOrUpdater;
    return { _chats: { ...state._chats, [chatId]: { ...prev, completedStatuses: next } } };
  }),

  addCompletedStatus: (chatId, statusKey) => set(state => {
    const prev = state._chats[chatId] ?? DEFAULT_CHAT();
    const existing = prev.completedStatuses ?? [];
    if (existing.includes(statusKey)) return state; // no-op
    return {
      _chats: {
        ...state._chats,
        [chatId]: { ...prev, completedStatuses: [...existing, statusKey] },
      },
    };
  }),

  // ── Terminal flags (NEW — key fix) ─────────────────────────────────────────
  setTaskCompleted: (chatId, val) => get()._patch(chatId, { taskCompleted: val }),

  setOrderCancelled: (chatId, reason) => get()._patch(chatId, {
    orderCancelled: true,
    cancellationReason: reason ?? null,
  }),

  // ── Reset for new session ──────────────────────────────────────────────────
  clearChatOrder: (chatId) => set(state => ({
    _chats: {
      ...state._chats,
      [chatId]: DEFAULT_CHAT(),
    },
  })),

  clearChat: (chatId) => set(state => {
    const chats = { ...state._chats };
    delete chats[chatId];
    return { _chats: chats };
  }),

  setMessages: (chatId, messages) => set(state => {
    const prev = state._chats[chatId] ?? DEFAULT_CHAT();
    return { _chats: { ...state._chats, [chatId]: { ...prev, messages } } };
  }),

  getMessages: (chatId) =>
    get()._chats[chatId]?.messages ?? [],

  clearMessages: (chatId) => set(state => {
    const prev = state._chats[chatId] ?? DEFAULT_CHAT();
    return { _chats: { ...state._chats, [chatId]: { ...prev, messages: [] } } };
  }),
  clearPersistedChat: (chatId) => {
    set(state => {
      const chats = { ...state._chats };
      delete chats[chatId];
      return { _chats: chats };
    });
  },

  _reset: () => set({ _chats: {} }),
}),
  {
    name: 'sendrey-order-store',
    // version: 2,
    storage: createJSONStorage(() => localStorage),
    onRehydrateStorage: () => (state) => {
      state?.setHasHydrated();
    },
    partialize: (state) => ({
      activeChatId: state.activeChatId,
      _chats: Object.fromEntries(
        Object.entries(state._chats).map(([chatId, chat]) => [
          chatId,
          {
            ...chat,
            currentOrder: chat.currentOrder,
          }
        ])
      )
    }),
  }
));

// Initialize synchronously if data already exists
const stored = localStorage.getItem('sendrey-order-store');
if (stored) {
  useOrderStore.setState({ _hasHydrated: true });
}

export default useOrderStore;